import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Stack,
  TextField,
  Dialog,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { Event, Resource, ResourceType } from '@/types/events';
import { v4 as uuidv4 } from 'uuid';

interface ResourceManagementProps {
  event: Event;
  onUpdate: (updatedEvent: Event) => void;
}

export const ResourceManagement: React.FC<ResourceManagementProps> = ({
  event,
  onUpdate
}) => {
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleAddResource = () => {
    setSelectedResource({
      id: uuidv4(),
      name: '',
      type: 'equipment',
      quantity: 1,
      available: 1,
      url: '',
      notes: ''
    });
    setIsDialogOpen(true);
  };

  const handleEditResource = (resource: Resource) => {
    setSelectedResource(resource);
    setIsDialogOpen(true);
  };

  const handleDeleteResource = (resourceId: string) => {
    const updatedResources = event.resources.filter(r => r.id !== resourceId);
    onUpdate({ ...event, resources: updatedResources });
  };

  const handleSaveResource = (resource: Resource) => {
    const updatedResources = selectedResource?.id
      ? event.resources.map(r => r.id === resource.id ? resource : r)
      : [...event.resources, resource];
    onUpdate({ ...event, resources: updatedResources });
    setIsDialogOpen(false);
    setSelectedResource(null);
  };

  const getResourceTypeColor = (type: ResourceType) => {
    switch (type) {
      case 'equipment':
        return 'primary';
      case 'material':
        return 'secondary';
      case 'venue':
        return 'success';
      case 'personnel':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Resource Management</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddResource}
          >
            Add Resource
          </Button>
        </Stack>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Resource</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell align="right">Available</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {event.resources.map((resource) => (
                <TableRow key={resource.id}>
                  <TableCell>
                    <Typography variant="subtitle2">
                      {resource.name}
                    </Typography>
                    {resource.vendor && (
                      <Typography variant="caption" color="text.secondary">
                        Vendor: {resource.vendor.name}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={resource.type}
                      color={getResourceTypeColor(resource.type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">{resource.quantity}</TableCell>
                  <TableCell align="right">{resource.available}</TableCell>
                  <TableCell>{resource.notes}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleEditResource(resource)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteResource(resource.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {selectedResource?.id ? 'Edit Resource' : 'Add Resource'}
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Name"
              value={selectedResource?.name || ''}
              onChange={(e) => setSelectedResource(prev => 
                prev ? { ...prev, name: e.target.value } : null
              )}
              fullWidth
            />
            <TextField
              select
              label="Type"
              value={selectedResource?.type || 'equipment'}
              onChange={(e) => setSelectedResource(prev => 
                prev ? { ...prev, type: e.target.value as ResourceType } : null
              )}
              fullWidth
            >
              <MenuItem value="equipment">Equipment</MenuItem>
              <MenuItem value="material">Material</MenuItem>
              <MenuItem value="venue">Venue</MenuItem>
              <MenuItem value="personnel">Personnel</MenuItem>
            </TextField>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Quantity"
                  type="number"
                  value={selectedResource?.quantity || 0}
                  onChange={(e) => setSelectedResource(prev => 
                    prev ? { ...prev, quantity: parseInt(e.target.value) || 0 } : null
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Available"
                  type="number"
                  value={selectedResource?.available || 0}
                  onChange={(e) => setSelectedResource(prev => 
                    prev ? { ...prev, available: parseInt(e.target.value) || 0 } : null
                  )}
                  fullWidth
                />
              </Grid>
            </Grid>
            <TextField
              label="Notes"
              value={selectedResource?.notes || ''}
              onChange={(e) => setSelectedResource(prev => 
                prev ? { ...prev, notes: e.target.value } : null
              )}
              multiline
              rows={2}
              fullWidth
            />
            {selectedResource?.type !== 'personnel' && (
              <Box sx={{ border: 1, borderColor: 'divider', p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Vendor Information
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Vendor Name"
                    value={selectedResource?.vendor?.name || ''}
                    onChange={(e) => setSelectedResource(prev => 
                      prev ? {
                        ...prev,
                        vendor: {
                          ...prev.vendor,
                          name: e.target.value
                        }
                      } : null
                    )}
                    fullWidth
                  />
                  <TextField
                    label="Contact"
                    value={selectedResource?.vendor?.contactName || ''}
                    onChange={(e) => setSelectedResource(prev => 
                      prev ? {
                        ...prev,
                        vendor: {
                          ...prev.vendor,
                          contact: e.target.value
                        }
                      } : null
                    )}
                    fullWidth
                  />
                  <TextField
                    label="Email"
                    type="email"
                    value={selectedResource?.vendor?.email || ''}
                    onChange={(e) => setSelectedResource(prev => 
                      prev ? {
                        ...prev,
                        vendor: {
                          ...prev.vendor,
                          email: e.target.value
                        }
                      } : null
                    )}
                    fullWidth
                  />
                </Stack>
              </Box>
            )}
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => selectedResource && handleSaveResource(selectedResource)}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
};